import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Global, css } from '@emotion/core';
import { ThemeProvider, useTheme } from 'emotion-theming';
import composeBuildTheme, { pickSkinFields } from '../../shared/theme/composeBuildTheme';
import { FOCUS_FILL, FOCUS_FILL_DARK } from '../../shared/theme/focusStyles';
import useBuildIsDark from './hooks/useBuildIsDark';

/** Light values for the toast CSS variables -- the literals toastManager and
 * AppWrapper's toast container used before they read variables. Outside Build
 * the variables are unset and those fallbacks apply. */
const TOAST_LIGHT = {
  surface: '#ffffff',
  text: '#333333',
  muted: '#999999',
  hover: '#f9f9f9',
  accent: '#4A90E2',
};

/**
 * Build's theme provider. Everything the calendar module renders -- the
 * calendar itself and the Create Calendar route -- sits under this so the
 * Appearance preference, the light high-contrast layer and the org skin apply
 * uniformly. Also publishes the CSS variables read by code that cannot see the
 * Emotion theme: the focusStyles helpers (plain CSS strings) and the toasts
 * (rendered by AppWrapper's container, outside this provider).
 */
const BuildThemeProvider = ({ children }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isDark = useBuildIsDark();
  /* The active rail icon takes the organisation's accent when it reads clearly
   * against this mode's surface, else the Build orange (see pickActiveAccent). */
  const skinAccent = currentUser?.userSkin?.accent;
  /* Build replaces the app theme, so the skin fields AppWrapper put there
   * (TV mode reads colorPrimary / isPrimaryDarkTheme) are carried across.
   * Keyed by value: AppWrapper rebuilds its theme object every render. */
  const skinFields = pickSkinFields(useTheme());
  const skinFieldsKey = JSON.stringify(skinFields);
  /* Memoized so the provider gets a stable object: a fresh theme every render
   * would invalidate emotion's cache and restyle every descendant. */
  const theme = useMemo(
    () => composeBuildTheme({ isDark, skinAccent, skinFields }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key stands in for skinFields
    [isDark, skinAccent, skinFieldsKey],
  );
  const { colors } = theme;
  const toast = isDark ? {
    surface: colors.surfaceElevated,
    text: colors.text,
    muted: colors.textSecondary,
    hover: colors.surfaceSecondary,
    accent: colors.hover,
  } : TOAST_LIGHT;

  return (
    <ThemeProvider theme={theme}>
      <Global
        styles={css`
          :root {
            --tb-focus-fill: ${isDark ? FOCUS_FILL_DARK : FOCUS_FILL};
            --tb-toast-surface: ${toast.surface};
            --tb-toast-text: ${toast.text};
            --tb-toast-muted: ${toast.muted};
            --tb-toast-hover: ${toast.hover};
            --tb-toast-accent: ${toast.accent};
          }
          /* The global stylesheet paints html/body #f0f0f0 and #app white. Week
             view overflows the viewport and the document itself scrolls, while
             the page wrappers stay viewport-wide, so past the first viewport only
             the document background shows between the day columns. In dark all
             three take the app background so nothing light scrolls in. */
          ${isDark ? `html, body, #app { background-color: ${colors.backgroundApp}; }` : ''}
        `}
      />
      {children}
    </ThemeProvider>
  );
};

BuildThemeProvider.propTypes = { children: PropTypes.node.isRequired };

export default BuildThemeProvider;
